import { Globe, Database, Brain, Search, FileText, Sparkles } from "lucide-react";

/**
 * Transparency Data Module
 * 
 * Contains all agent definitions, prompts, and flow descriptions
 * for the transparency feature. Designed to be executive-friendly
 * with plain-English explanations.
 */

export type AgentId = "collector" | "processor" | "evaluator";

export interface AgentDefinition {
  id: AgentId;
  name: string;
  subtitle: string;
  description: string;
  whatItDoes: string[];
  model: string | null;
  modelDescription: string | null;
  tools: string[];
  receives: string;
  outputs: string;
  icon: "globe" | "database" | "brain";
  color: string;
  prompt: string | null;
  promptSummary: string | null;
}

export interface DataFlow {
  from: AgentId;
  to: AgentId;
  label: string;
  description: string;
}

export const AGENTS: Record<AgentId, AgentDefinition> = {
  collector: {
    id: "collector",
    name: "Collector",
    subtitle: "Collector",
    description: "Discovers and gathers information from vendor websites",
    whatItDoes: [
      "Runs a two-phase search: first for documentation, then general pages",
      "Prioritizes docs, API references, guides, and getting-started pages",
      "Scrapes each discovered URL and extracts clean markdown content",
      "Deduplicates results and tracks scraping success/failures",
      "Stores all discovered links and page versions in Firestore",
    ],
    model: null,
    modelDescription: null,
    tools: ["Tavily Search API", "Firecrawl Web Scraper", "Firestore Database"],
    receives: "A vendor's website URL",
    outputs: "Clean, structured markdown content from up to 100 web pages",
    icon: "globe",
    color: "#D4893E", // Unified gold/navy
    prompt: `TAVILY SEARCH QUERIES
====================

1. Documentation Search Query:
   "site:{domain} (documentation OR "API reference" OR "user guide" OR "getting started" OR docs)"
   
   This query specifically targets documentation pages, which typically contain
   the most valuable technical information about a vendor's product.

2. General Search Query:
   Direct URL search with configurable depth (basic or advanced)
   
   This finds the main pages, blog posts, and other content on the vendor's site.

URL PRIORITIZATION PATTERNS
===========================

The Collector boosts relevance scores for URLs matching these patterns:
• /docs/ or /documentation/
• /api-reference/ or /api/
• /help/ or /guide/
• /manual/ or /reference/
• /tutorial/ or /getting-started/
• /quickstart/
• Subdomains: docs.*, help.*, developer.*

FIRECRAWL CONFIGURATION
=======================

Each URL is scraped with these settings:
• Format: markdown (clean text extraction)
• Timeout: 30 seconds per page
• Automatic removal of navigation, ads, and boilerplate

The scraper extracts:
• Page title from metadata
• Full markdown content
• Word count for analysis`,
    promptSummary: "The Collector uses search queries to find documentation and important pages, then scrapes them into clean markdown. It prioritizes technical docs, API references, and guides over marketing content.",
  },

  processor: {
    id: "processor",
    name: "Processor",
    subtitle: "Processor",
    description: "Organizes and stores vendor information for intelligent retrieval",
    whatItDoes: [
      "Combines all collected pages into a unified knowledge base",
      "Uploads content to Google's Gemini File Search",
      "Enables natural language queries against the vendor data",
      "Provides source citations for every answer",
    ],
    model: "Gemini 3.1 Pro",
    modelDescription: "Google's advanced AI model optimized for retrieval and summarization tasks",
    tools: ["Google Gemini File Search API"],
    receives: "Collected web pages from the Collector",
    outputs: "A searchable knowledge base that can answer questions about the vendor",
    icon: "database",
    color: "#D4893E", // Unified gold/navy
    prompt: null,
    promptSummary: "This agent uses Google's built-in retrieval system. No custom prompts are needed—it intelligently finds and synthesizes relevant information.",
  },

  evaluator: {
    id: "evaluator",
    name: "Evaluator",
    subtitle: "Evaluator",
    description: "Evaluates vendors using a rigorous 6-criterion framework",
    whatItDoes: [
      "Queries the Processor for specific information about each criterion",
      "Evaluates transparency, customizability, agility, utility, learning, and portability",
      "Scores each criterion as Yes, Partially, No, or Insufficient Information",
      "Generates an executive summary with key trade-offs",
      "Creates strategic questions for decision-makers",
    ],
    model: "Claude Opus 4.7",
    modelDescription: "Anthropic's most capable AI model with advanced reasoning and structured output capabilities",
    tools: ["Anthropic Claude API with Structured Output"],
    receives: "Access to the vendor knowledge base via Processor",
    outputs: "A comprehensive evaluation report with scores, evidence, and recommendations",
    icon: "brain",
    color: "#D4893E", // Unified gold/navy
    prompt: `You are an expert analyst helping Fortune 100 executives evaluate B2B AI-powered SaaS software vendors. You generate evaluation reports grounded in a distinctive framework that assesses vendors not only on immediate utility, but on whether they accelerate or impede the organization's long-term capacity to work effectively with AI.

You are an analyst, not a decision-maker. Your role is to illuminate trade-offs and provide information that supports informed decisions—not to make buy/don't buy recommendations.

FOUNDATIONAL PHILOSOPHY

AI-powered SaaS is fundamentally different from traditional SaaS:

1. The Economics Have Changed: AI SaaS introduces meaningful cost of goods sold (compute, inference, API calls). This creates misaligned incentives—vendors may quietly optimize for reduced usage or cheaper inference paths.

2. The Moat Has Eroded: Core AI functionality now rests on commodity foundation models. This creates strong vendor incentives to manufacture artificial lock-in through proprietary data formats and tool-specific workflows.

3. The Stakes Are Organizational: The companies that thrive will develop deep organizational fluency in AI. A vendor solution that delivers a quick win may simultaneously delay the organizational learning that will matter far more in the long run.

SCORING METHODOLOGY

For each criterion, assign one of these scores:
• Yes: Clear, verifiable evidence that this criterion is met
• Partially: Some evidence exists, but with meaningful gaps or caveats
• No: Clear evidence that this criterion is not met
• Insufficient Information: Not enough information to assess

CRITICAL RULES

1. Default to "Insufficient Information" when uncertain. Do not infer or speculate.
2. Frame missing information as questions for the vendor.
3. Never fabricate information.
4. Distinguish claims from evidence—marketing claims are not evidence.
5. Present trade-offs, not verdicts.`,
    promptSummary: "The Evaluator uses a detailed prompt that positions it as an expert analyst focused on illuminating trade-offs rather than making recommendations. It's designed to be skeptical of marketing claims and to highlight organizational implications.",
  },
};

export const EVALUATION_CRITERIA = [
  {
    id: "SEE",
    name: "SEE",
    question: "Can you see how the AI works?",
    subtitle: "Transparency & Visibility",
    description: "Evaluates whether you can see the prompts, models, and architecture being used.",
  },
  {
    id: "CHANGE",
    name: "CHANGE",
    question: "Can you modify AI behavior?",
    subtitle: "Customizability & Control",
    description: "Assesses your ability to adjust prompts, models, and configurations.",
  },
  {
    id: "ADAPT",
    name: "ADAPT",
    question: "Does the vendor keep pace with AI advancement?",
    subtitle: "Vendor Agility & Innovation",
    description: "Measures how quickly the vendor adopts new AI capabilities.",
  },
  {
    id: "USE",
    name: "USE",
    question: "Is the tool actually useful?",
    subtitle: "Genuine Utility & Value",
    description: "Determines if the tool provides real value versus the appearance of value.",
  },
  {
    id: "LEARN",
    name: "LEARN",
    question: "Do you build portable AI skills?",
    subtitle: "Transferable Knowledge",
    description: "Evaluates whether using the tool builds generalizable AI capabilities.",
  },
  {
    id: "EXIT",
    name: "EXIT",
    question: "Can you leave cleanly?",
    subtitle: "Portability & Migration",
    description: "Assesses the difficulty of transitioning away from the vendor.",
  },
];

export const DATA_FLOWS: DataFlow[] = [
  {
    from: "collector",
    to: "processor",
    label: "Web Content",
    description: "The Collector sends all scraped and cleaned web page content to the Processor for storage.",
  },
  {
    from: "processor",
    to: "evaluator",
    label: "Knowledge Access",
    description: "The Evaluator queries the Processor's knowledge base to gather evidence for each criterion.",
  },
];

export const AGENT_ORDER: AgentId[] = ["collector", "processor", "evaluator"];

export function getAgentIcon(icon: AgentDefinition["icon"]) {
  switch (icon) {
    case "globe":
      return Globe;
    case "database":
      return Database;
    case "brain":
      return Brain;
    default:
      return Sparkles;
  }
}
